import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import AuthService from '../utils/authService';
import '../styles/neumorphism.css';

const { FiPlus, FiEdit2, FiTrash2, FiActivity, FiSave, FiX, FiLoader, FiUsers } = FiIcons;

function DepartmentManagement() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const departmentsData = await AuthService.getAllDepartments();
      setDepartments(departmentsData);
    } catch (error) {
      console.error('Error loading departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = async (departmentData) => {
    try {
      await AuthService.createDepartment(departmentData);
      await loadDepartments();
      setShowDepartmentForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating department:', error);
      alert('Fehler beim Erstellen der Abteilung');
    }
  };

  const handleUpdateDepartment = async (departmentData) => {
    try {
      await AuthService.updateDepartment(editingDepartment.id, departmentData);
      await loadDepartments();
      setShowDepartmentForm(false);
      setEditingDepartment(null);
      resetForm();
    } catch (error) {
      console.error('Error updating department:', error);
      alert('Fehler beim Aktualisieren der Abteilung');
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (confirm('Möchten Sie diese Abteilung wirklich löschen? Alle zugehörigen Daten werden ebenfalls gelöscht.')) {
      try {
        await AuthService.deleteDepartment(departmentId);
        await loadDepartments();
      } catch (error) {
        console.error('Error deleting department:', error);
        alert('Fehler beim Löschen der Abteilung');
      }
    }
  };

  const startEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      description: department.description || ''
    });
    setShowDepartmentForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingDepartment) {
      handleUpdateDepartment(formData);
    } else {
      handleCreateDepartment(formData);
    }
  };

  // Nur Admins können die Abteilungsverwaltung sehen
  if (!user || user.role !== 'admin') {
    return (
      <div className="p-6 text-center">
        <SafeIcon icon={FiActivity} className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Zugriff verweigert</h3>
        <p className="text-gray-600">Sie haben keine Berechtigung für die Abteilungsverwaltung.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Abteilungsverwaltung</h1>
          <p className="mt-2 text-gray-600">Verwalten Sie Abteilungen und deren Konfigurationen</p>
        </div>
        <button
          onClick={() => setShowDepartmentForm(true)}
          className="flex items-center px-4 py-2 neu-button bg-orange-50 text-orange-700"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
          Abteilung hinzufügen
        </button>
      </div>

      <div className="neu-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Abteilungen</h3>
        </div>

        {departments.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiActivity} className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Abteilungen</h3>
            <p className="text-gray-500">Erstellen Sie die erste Abteilung</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Abteilung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Beschreibung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {departments.map((department) => (
                  <tr key={department.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 neu-element rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiActivity} className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{department.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{department.description || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {department.created_at ? new Date(department.created_at).toLocaleDateString('de-DE') : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => startEdit(department)}
                          className="text-orange-600 hover:text-orange-900 p-2 neu-button"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDepartment(department.id)}
                          className="text-red-600 hover:text-red-900 p-2 neu-button"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Department Form Modal */}
      {showDepartmentForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="neu-card bg-white max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingDepartment ? 'Abteilung bearbeiten' : 'Neue Abteilung erstellen'}
              </h2>
              <button
                onClick={() => {
                  setShowDepartmentForm(false);
                  setEditingDepartment(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 p-2 neu-button"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Abteilungsname *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="neu-input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beschreibung
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="neu-input w-full"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepartmentForm(false);
                    setEditingDepartment(null);
                    resetForm();
                  }}
                  className="px-4 py-2 neu-button text-gray-700"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 neu-button bg-orange-50 text-orange-700"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingDepartment ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentManagement;